/** Facilities maintenance: what's due or overdue, and the preventive maintenance and inspection schedules that open that work. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarCheck, CalendarClock, CalendarPlus, Hourglass, Pause, Pencil, Play, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { AssigneeSelect, CategorySelect, EquipmentSelect, InspectionStatusBadge, PlaceFields, ScheduleStateBadge } from '../../components/facilities-ui';
import { useToast } from '../../components/toast';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  cn,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  PageHeader,
  PageLoader,
  Segmented,
  Select,
  Switch,
  Table,
  Tabs,
  Td,
  Textarea,
  Th,
} from '../../components/ui';
import { PriorityBadge, WorkOrderStatusBadge } from '../../components/work-order-badges';
import { api, errorMessage } from '../../lib/api';
import { FREQUENCY_LABEL, MONTHS, WEEKDAYS } from '../../lib/automation';
import { useDesk } from '../../lib/desk';
import {
  equipmentLabel,
  INSPECTION_KIND_LABEL,
  placeLabel,
  SCHEDULE_KIND_LABEL,
  useEquipmentOptions,
  useFacilityPlaces,
  useInspectionTemplates,
  type DueItem,
  type DueWork,
  type FacilitySchedule,
  type FacilityScheduleDetail,
  type FacilityScheduleKind,
  type InspectionStatus,
  type ScheduleInput,
  type ScheduleList,
  type ScheduleOccurrence,
  type SchedulePreview,
} from '../../lib/facilities';
import { formatDate, formatDue, formatUtcDate, toDateInput } from '../../lib/format';
import { useDebouncedValue, useUrlState } from '../../lib/hooks';
import type { RecurrenceFrequency, WorkOrderPriority, WorkOrderStatus } from '../../lib/types';
import { WORK_ORDER_PRIORITY_LABEL } from '../../lib/work-orders';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SECTION_LABEL = 'text-xs font-semibold tracking-wide text-slate-500 uppercase';

// Due dates are days on the school's calendar (YYYY-MM-DD). Format them with formatUtcDate and do day math in UTC,
// so they never shift a day; formatDate is only for timestamps.
const relativeFormat = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });

const utcDay = (iso: string) => new Date(`${iso}T00:00:00Z`);

/** "today", "tomorrow", "in 5 days", "3 days ago" */
const relativeDay = (iso: string, today: string) =>
  relativeFormat.format(Math.round((utcDay(iso).getTime() - utcDay(today).getTime()) / 86_400_000), 'day');

function shiftDay(iso: string, days: number) {
  const date = utcDay(iso);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

const workOrderPath = (id: string) => `/facilities/work-orders/${id}`;
const inspectionPath = (id: string) => `/facilities/inspections/${id}`;
const equipmentPath = (id: string) => `/facilities/equipment/${id}`;
const occurrencePath = (occurrence: ScheduleOccurrence) =>
  occurrence.type === 'WORK_ORDER' ? workOrderPath(occurrence.id) : inspectionPath(occurrence.id);

/** POST /facilities/schedules/:id/open */
type OpenedOccurrence = Pick<ScheduleOccurrence, 'type' | 'id' | 'number' | 'title'>;

type Tab = 'due' | 'schedules';
type KindFilter = 'ALL' | FacilityScheduleKind;
const DAY_OPTIONS = ['7', '14', '30', '60'] as const;
type Days = (typeof DAY_OPTIONS)[number];

// ---------------------------------------------------------------------------
// Due and overdue
// ---------------------------------------------------------------------------

const isOverdue = (item: DueItem, today: string) => item.overdue || item.dueOn < today;

function DueRow({ item, today, schedulesHref }: { item: DueItem; today: string; schedulesHref: string }) {
  const overdue = isOverdue(item, today);
  const place = placeLabel(item);
  return (
    <li className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center sm:gap-6">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
          {item.type === 'WORK_ORDER' && (
            <>
              <Link to={workOrderPath(item.id)} className="font-medium text-brand-700 hover:underline">
                Work order #{item.number}
              </Link>
              {item.status && <WorkOrderStatusBadge status={item.status as WorkOrderStatus} />}
            </>
          )}
          {item.type === 'INSPECTION' && (
            <>
              <Link to={inspectionPath(item.id)} className="font-medium text-brand-700 hover:underline">
                Inspection
              </Link>
              {item.status && <InspectionStatusBadge status={item.status as InspectionStatus} />}
            </>
          )}
          {item.type === 'UPCOMING' && (
            <>
              <Link to={schedulesHref} className="font-medium text-brand-700 hover:underline" title="A schedule opens this before it’s due">
                {item.opensOn && item.opensOn > today ? `Opens ${formatUtcDate(item.opensOn)}` : item.waitingOn ? 'Not opened yet' : 'Opens today'}
              </Link>
              {item.waitingOn && (
                <Link
                  to={occurrencePath(item.waitingOn)}
                  className="inline-flex items-center gap-1 font-medium text-amber-800 hover:underline"
                  title="It opens once the one before it is finished. If that’s still open after this one’s due date, this one is skipped."
                >
                  <Hourglass className="size-3.5" aria-hidden />
                  {item.waitingOn.type === 'WORK_ORDER' ? `Waiting on work order #${item.waitingOn.number}` : 'Waiting on the last inspection'}
                </Link>
              )}
            </>
          )}
        </div>
        <p className="mt-0.5 text-sm font-medium break-words text-slate-900">{item.title}</p>
        {(place || item.facilityAsset) && (
          <p className="mt-0.5 text-xs text-slate-500">
            {place}
            {place && item.facilityAsset && ' · '}
            {item.facilityAsset && (
              <Link to={equipmentPath(item.facilityAsset.id)} className="text-brand-700 hover:underline">
                {equipmentLabel(item.facilityAsset)}
              </Link>
            )}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:shrink-0 sm:justify-end">
        {item.priority && <PriorityBadge priority={item.priority} />}
        <span className={item.assignedTo ? 'text-slate-700' : 'text-slate-400'}>{item.assignedTo?.name ?? 'Unassigned'}</span>
        <span className={cn('whitespace-nowrap', overdue ? 'font-medium text-red-700' : 'text-slate-700')}>
          Due {formatUtcDate(item.dueOn)}
          <span className="font-normal text-slate-500"> · {relativeDay(item.dueOn, today)}</span>
        </span>
      </div>
    </li>
  );
}

function DueTab({
  days,
  mine,
  buildingId,
  onFilter,
}: {
  days: Days;
  mine: boolean;
  buildingId: string;
  onFilter(patch: { days?: string; mine?: string; building?: string }): void;
}) {
  const desk = useDesk();
  // Facilities work only goes to technicians and admins.
  const canHaveWork = desk.can('TECHNICIAN');
  const onlyMine = mine && canHaveWork;
  const places = useFacilityPlaces();
  const [params] = useSearchParams();
  // Keep this tab's filters in the link, so coming back from Schedules lands on the same view.
  const schedulesHref = useMemo(() => {
    const next = new URLSearchParams(params);
    next.set('tab', 'schedules');
    return `?${next.toString()}`;
  }, [params]);

  const query = { days: Number(days), assignedTo: onlyMine ? 'me' : undefined, buildingId: buildingId || undefined };
  const due = useQuery({
    queryKey: ['facilities', 'due', query],
    queryFn: () => api.get<DueWork>('/facilities/due', query),
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  });
  const data = due.data;

  const groups = data
    ? [
        { key: 'overdue', title: 'Overdue', tone: 'red' as const, items: data.items.filter((item) => isOverdue(item, data.today)) },
        {
          key: 'today',
          title: 'Due today',
          tone: 'amber' as const,
          items: data.items.filter((item) => !isOverdue(item, data.today) && item.dueOn === data.today),
        },
        {
          key: 'upcoming',
          title: 'Coming up',
          tone: 'gray' as const,
          items: data.items.filter((item) => !isOverdue(item, data.today) && item.dueOn !== data.today),
        },
      ].filter((group) => group.items.length > 0)
    : [];

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-slate-100 px-5 py-3">
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <span className="text-sm whitespace-nowrap text-slate-500">Look ahead</span>
          <Segmented<Days>
            className="flex-1 sm:w-64 sm:flex-none"
            value={days}
            onChange={(value) => onFilter({ days: value })}
            options={DAY_OPTIONS.map((value) => ({ value, label: `${value} days` }))}
          />
        </div>
        <Select className="sm:w-56" value={buildingId} onChange={(event) => onFilter({ building: event.target.value })} aria-label="Building">
          <option value="">All buildings</option>
          {places.data?.map((building) => (
            <option key={building.id} value={building.id}>
              {building.name}
            </option>
          ))}
        </Select>
        {canHaveWork && (
          <div className="sm:ml-auto">
            <Switch checked={onlyMine} onChange={(value) => onFilter({ mine: value ? '1' : '' })} label="Only mine" />
          </div>
        )}
      </div>

      {!data ? (
        due.error ? (
          <ErrorState error={due.error} onRetry={() => void due.refetch()} />
        ) : (
          <PageLoader />
        )
      ) : groups.length === 0 ? (
        <EmptyState
          icon={<CalendarCheck />}
          title="Nothing due"
          description={`Nothing${onlyMine ? ' assigned to you' : ''}${buildingId ? ' in this building' : ''} is overdue or due in the next ${days} days.`}
        />
      ) : (
        <div className={cn('transition-opacity', due.isPlaceholderData && 'opacity-60')}>
          {groups.map((group) => (
            <section key={group.key} aria-labelledby={`due-${group.key}`} className="border-t border-slate-100 first:border-t-0">
              <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/80 px-5 py-2">
                <h2 id={`due-${group.key}`} className={SECTION_LABEL}>
                  {group.title}
                </h2>
                <Badge tone={group.tone}>{group.items.length}</Badge>
              </div>
              <ul className="divide-y divide-slate-100">
                {group.items.map((item) => (
                  <DueRow key={`${item.type}-${item.id}`} item={item} today={data.today} schedulesHref={schedulesHref} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Schedule form
// ---------------------------------------------------------------------------

/** Numbers stay strings while editing, so a field can be cleared and retyped. */
interface ScheduleForm {
  kind: FacilityScheduleKind;
  name: string;
  description: string;
  enabled: boolean;
  facilityAssetId: string;
  buildingId: string;
  roomId: string;
  departmentId: string;
  priority: WorkOrderPriority;
  checklist: string;
  estimatedMinutes: string;
  templateId: string;
  assignedToId: string;
  frequency: RecurrenceFrequency;
  interval: string;
  weekdays: number[];
  dayOfMonth: string;
  month: string;
  startDate: string;
  endDate: string;
  leadDays: string;
}

const UNIT: Record<RecurrenceFrequency, [string, string]> = {
  DAILY: ['day', 'days'],
  WEEKLY: ['week', 'weeks'],
  MONTHLY: ['month', 'months'],
  YEARLY: ['year', 'years'],
};

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** A whole number from a number input, or null when it's blank. */
function toInt(value: string) {
  const number = Number(value);
  return value.trim() !== '' && Number.isInteger(number) ? number : null;
}

function toForm(schedule: FacilitySchedule | null): ScheduleForm {
  const startDate = schedule?.startDate ?? toDateInput(new Date());
  const start = utcDay(startDate);
  return {
    kind: schedule?.kind ?? 'PREVENTIVE_MAINTENANCE',
    name: schedule?.name ?? '',
    description: schedule?.description ?? '',
    enabled: schedule?.enabled ?? true,
    facilityAssetId: schedule?.facilityAssetId ?? '',
    buildingId: schedule?.buildingId ?? '',
    roomId: schedule?.roomId ?? '',
    departmentId: schedule?.departmentId ?? '',
    priority: schedule?.priority ?? 'NORMAL',
    checklist: schedule?.checklist.join('\n') ?? '',
    estimatedMinutes: schedule?.estimatedMinutes ? String(schedule.estimatedMinutes) : '',
    templateId: schedule?.templateId ?? '',
    assignedToId: schedule?.assignedToId ?? '',
    frequency: schedule?.frequency ?? 'MONTHLY',
    interval: String(schedule?.interval ?? 1),
    // Blank on the server means "the start date's": show that choice explicitly.
    weekdays: schedule?.weekdays.length ? schedule.weekdays : [start.getUTCDay()],
    dayOfMonth: String(schedule?.dayOfMonth ?? start.getUTCDate()),
    month: String(schedule?.month ?? start.getUTCMonth() + 1),
    startDate,
    endDate: schedule?.endDate ?? '',
    leadDays: String(schedule?.leadDays ?? 7),
  };
}

/** The fields that decide the due dates, as POST /facilities/schedules/preview takes them. */
function recurrenceOf(form: ScheduleForm) {
  const monthly = form.frequency === 'MONTHLY' || form.frequency === 'YEARLY';
  return {
    frequency: form.frequency,
    interval: toInt(form.interval) ?? 1,
    weekdays: form.frequency === 'WEEKLY' ? [...form.weekdays].sort((a, b) => a - b) : [],
    dayOfMonth: monthly ? toInt(form.dayOfMonth) : null,
    month: form.frequency === 'YEARLY' ? toInt(form.month) : null,
    startDate: form.startDate,
    endDate: form.endDate || null,
  };
}

type Recurrence = ReturnType<typeof recurrenceOf>;

const inRange = (value: number | null, min: number, max: number) => value === null || (value >= min && value <= max);

function recurrenceValid(recurrence: Recurrence) {
  return (
    DATE_PATTERN.test(recurrence.startDate) &&
    (!recurrence.endDate || (DATE_PATTERN.test(recurrence.endDate) && recurrence.endDate >= recurrence.startDate)) &&
    inRange(recurrence.interval, 1, 365) &&
    inRange(recurrence.dayOfMonth, 1, 31) &&
    inRange(recurrence.month, 1, 12)
  );
}

function toInput(form: ScheduleForm): ScheduleInput {
  const maintenance = form.kind === 'PREVENTIVE_MAINTENANCE';
  return {
    kind: form.kind,
    name: form.name.trim(),
    description: form.description.trim() || null,
    enabled: form.enabled,
    facilityAssetId: form.facilityAssetId || null,
    buildingId: form.buildingId || null,
    roomId: form.roomId || null,
    departmentId: maintenance ? form.departmentId || null : null,
    priority: form.priority,
    checklist: maintenance
      ? form.checklist
          .split('\n')
          .map((step) => step.trim())
          .filter(Boolean)
      : [],
    estimatedMinutes: maintenance ? toInt(form.estimatedMinutes) : null,
    templateId: maintenance ? null : form.templateId || null,
    assignedToId: form.assignedToId || null,
    ...recurrenceOf(form),
    leadDays: toInt(form.leadDays) ?? 7,
  };
}

function ScheduleModal({ open, schedule, onClose }: { open: boolean; schedule: FacilitySchedule | null; onClose(): void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<ScheduleForm>(() => toForm(schedule));
  const [error, setError] = useState('');
  const errorRef = useRef<HTMLDivElement>(null);
  const update = (patch: Partial<ScheduleForm>) => setForm((current) => ({ ...current, ...patch }));
  const maintenance = form.kind === 'PREVENTIVE_MAINTENANCE';

  useEffect(() => {
    if (!open) return;
    setForm(toForm(schedule));
    setError('');
  }, [open, schedule]);

  // The form scrolls; bring a server error into view.
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [error]);

  const templates = useInspectionTemplates(false, open);
  const equipment = useEquipmentOptions(form.buildingId || undefined, open);

  // Debounced as a string, so re-renders with the same schedule don't restart the timer.
  const recurrenceKey = useDebouncedValue(JSON.stringify(recurrenceOf(form)), 400);
  const recurrence = useMemo(() => JSON.parse(recurrenceKey) as Recurrence, [recurrenceKey]);
  const previewable = open && recurrenceValid(recurrence);
  const preview = useQuery({
    queryKey: ['facilities', 'schedule-preview', recurrenceKey],
    queryFn: () => api.post<SchedulePreview>('/facilities/schedules/preview', recurrence),
    enabled: previewable,
    retry: false,
    placeholderData: (previous) => previous,
  });
  // Preview errors stay quiet; saving shows the server's message.
  const previewData = previewable && !preview.isError ? preview.data : undefined;
  const leadDays = toInt(form.leadDays);
  const firstDue = previewData?.dates[0];
  const firstOpens = firstDue && leadDays !== null && leadDays >= 0 && leadDays <= 60 ? shiftDay(firstDue, -leadDays) : null;

  const save = useMutation({
    mutationFn: (input: ScheduleInput) =>
      schedule
        ? api.put<FacilityScheduleDetail>(`/facilities/schedules/${schedule.id}`, input)
        : api.post<FacilityScheduleDetail>('/facilities/schedules', input),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      toast.success(schedule ? 'Schedule saved' : `Added “${saved.name}”`);
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError('');
    save.mutate(toInput(form));
  };

  // Equipment is filtered to the building, so a different building clears it.
  const choosePlace = (place: { buildingId: string; roomId: string }) =>
    update(place.buildingId && place.buildingId !== form.buildingId ? { ...place, facilityAssetId: '' } : place);

  // Equipment says where the work is: fill in its building and room when they aren't chosen yet.
  const chooseEquipment = (facilityAssetId: string) => {
    const option = equipment.data?.find((item) => item.id === facilityAssetId);
    if (option?.buildingId && (!form.buildingId || (option.buildingId === form.buildingId && !form.roomId))) {
      update({ facilityAssetId, buildingId: option.buildingId, roomId: form.roomId || option.roomId || '' });
    } else {
      update({ facilityAssetId });
    }
  };

  const assigneeField = (
    <Field label={maintenance ? 'Assign to' : 'Inspector'}>
      <AssigneeSelect value={form.assignedToId} onChange={(assignedToId) => update({ assignedToId })} enabled={open} />
    </Field>
  );
  const knownTemplate = templates.data?.some((template) => template.id === form.templateId);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={schedule ? `Edit ${schedule.name}` : 'New schedule'}
      description="Opens a work order or an inspection ahead of each due date."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" form="schedule-form" variant="primary" loading={save.isPending}>
            {schedule ? 'Save' : 'Add schedule'}
          </Button>
        </>
      }
    >
      <form id="schedule-form" onSubmit={submit} className="space-y-6">
        {error && (
          <div ref={errorRef}>
            <Alert tone="red">{error}</Alert>
          </div>
        )}

        <section className="space-y-4">
          <div>
            <span className="mb-1 block text-sm font-medium text-slate-700">What it opens</span>
            <Segmented<FacilityScheduleKind>
              value={form.kind}
              onChange={(kind) => update({ kind })}
              options={(Object.keys(SCHEDULE_KIND_LABEL) as FacilityScheduleKind[]).map((value) => ({ value, label: SCHEDULE_KIND_LABEL[value] }))}
            />
          </div>
          <Field label="Name" required>
            <Input
              value={form.name}
              maxLength={120}
              required
              autoFocus={!schedule}
              onChange={(event) => update({ name: event.target.value })}
              placeholder={maintenance ? 'e.g. Replace rooftop unit filters' : 'e.g. Monthly playground inspection'}
            />
          </Field>
          <Field label="Description" hint={maintenance ? 'Goes on each work order it opens.' : undefined}>
            <Textarea rows={2} maxLength={5000} value={form.description} onChange={(event) => update({ description: event.target.value })} />
          </Field>
        </section>

        <section className="space-y-3">
          <h3 className={SECTION_LABEL}>Where</h3>
          <div className="grid gap-3 sm:grid-cols-3">
            <PlaceFields buildingId={form.buildingId} roomId={form.roomId} onChange={choosePlace} />
            <Field label="Equipment">
              <EquipmentSelect value={form.facilityAssetId} buildingId={form.buildingId || undefined} onChange={chooseEquipment} />
            </Field>
          </div>
          <p className="text-xs text-slate-500">Picking equipment is enough: its building and room fill in.</p>
        </section>

        {maintenance ? (
          <section className="space-y-3">
            <h3 className={SECTION_LABEL}>Work order</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Category">
                <CategorySelect value={form.departmentId} onChange={(departmentId) => update({ departmentId })} />
              </Field>
              <Field label="Priority">
                <Select value={form.priority} onChange={(event) => update({ priority: event.target.value as WorkOrderPriority })}>
                  {(Object.keys(WORK_ORDER_PRIORITY_LABEL) as WorkOrderPriority[]).map((priority) => (
                    <option key={priority} value={priority}>
                      {WORK_ORDER_PRIORITY_LABEL[priority]}
                    </option>
                  ))}
                </Select>
              </Field>
              {assigneeField}
              <Field label="Estimated minutes" hint="Optional">
                <Input
                  type="number"
                  min={1}
                  max={10000}
                  step={1}
                  value={form.estimatedMinutes}
                  onChange={(event) => update({ estimatedMinutes: event.target.value })}
                />
              </Field>
            </div>
            <Field label="Checklist" hint="One step per line, up to 50.">
              <Textarea
                rows={5}
                value={form.checklist}
                onChange={(event) => update({ checklist: event.target.value })}
                placeholder={'Shut off power at the disconnect\nReplace the filters\nCheck the belt tension'}
              />
            </Field>
          </section>
        ) : (
          <section className="space-y-3">
            <h3 className={SECTION_LABEL}>Inspection</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Template"
                required
                hint={templates.data?.length === 0 ? 'There are no inspection templates yet. Add one first.' : 'Each inspection uses this template’s checklist.'}
              >
                <Select value={form.templateId} required onChange={(event) => update({ templateId: event.target.value })}>
                  <option value="">Choose a template</option>
                  {templates.data?.map((template) => (
                    <option key={template.id} value={template.id}>
                      {`${template.name} · ${INSPECTION_KIND_LABEL[template.kind]}`}
                    </option>
                  ))}
                  {form.templateId && templates.data && !knownTemplate && (
                    <option value={form.templateId}>
                      {schedule?.template?.id === form.templateId ? `${schedule.template.name} (archived)` : 'Selected template'}
                    </option>
                  )}
                </Select>
              </Field>
              {assigneeField}
            </div>
          </section>
        )}

        <section className="space-y-3">
          <h3 className={SECTION_LABEL}>Repeats</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Frequency">
              <Select value={form.frequency} onChange={(event) => update({ frequency: event.target.value as RecurrenceFrequency })}>
                {(Object.keys(FREQUENCY_LABEL) as RecurrenceFrequency[]).map((frequency) => (
                  <option key={frequency} value={frequency}>
                    {FREQUENCY_LABEL[frequency]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Every">
              <span className="flex items-center gap-2">
                <span className="w-24">
                  <Input type="number" min={1} max={365} step={1} required value={form.interval} onChange={(event) => update({ interval: event.target.value })} />
                </span>
                <span className="text-sm text-slate-600">{UNIT[form.frequency][toInt(form.interval) === 1 ? 0 : 1]}</span>
              </span>
            </Field>
          </div>

          {form.frequency === 'WEEKLY' && (
            <fieldset>
              <legend className="mb-1 block text-sm font-medium text-slate-700">On</legend>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {WEEKDAYS.map((day, index) => (
                  <label key={day} className="flex items-center gap-1.5 text-sm text-slate-700" title={day}>
                    <input
                      type="checkbox"
                      className="size-4 rounded border-slate-300"
                      checked={form.weekdays.includes(index)}
                      onChange={(event) =>
                        update({ weekdays: event.target.checked ? [...form.weekdays, index] : form.weekdays.filter((weekday) => weekday !== index) })
                      }
                    />
                    {day.slice(0, 3)}
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-slate-500">With none checked, it’s due on the start date’s weekday.</p>
            </fieldset>
          )}

          {(form.frequency === 'MONTHLY' || form.frequency === 'YEARLY') && (
            <div className="grid gap-3 sm:grid-cols-2">
              {form.frequency === 'YEARLY' && (
                <Field label="Month">
                  <Select value={form.month} onChange={(event) => update({ month: event.target.value })}>
                    {MONTHS.map((name, index) => (
                      <option key={name} value={String(index + 1)}>
                        {name}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              <Field label="Day of the month" hint={(toInt(form.dayOfMonth) ?? 0) > 28 ? 'In shorter months, it’s due on the last day.' : undefined}>
                <span className="block w-24">
                  <Input type="number" min={1} max={31} step={1} required value={form.dayOfMonth} onChange={(event) => update({ dayOfMonth: event.target.value })} />
                </span>
              </Field>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Starts" required>
              <Input type="date" required value={form.startDate} onChange={(event) => update({ startDate: event.target.value })} />
            </Field>
            <Field label="Ends" hint="Optional">
              <Input type="date" min={form.startDate || undefined} value={form.endDate} onChange={(event) => update({ endDate: event.target.value })} />
            </Field>
          </div>

          <Field label="Open it this many days before it’s due" hint="0 to 60 days">
            <span className="block w-24">
              <Input type="number" min={0} max={60} step={1} required value={form.leadDays} onChange={(event) => update({ leadDays: event.target.value })} />
            </span>
          </Field>

          <div className="rounded-lg bg-slate-50 p-3 text-sm" aria-live="polite">
            {previewData ? (
              <>
                <p className="font-medium text-slate-800">{previewData.recurrence}</p>
                {previewData.dates.length ? (
                  <>
                    <p className="mt-2 text-xs text-slate-500">Next due</p>
                    <ul className="grid gap-x-4 text-slate-600 sm:grid-cols-2">
                      {previewData.dates.map((date) => (
                        <li key={date}>{formatUtcDate(date)}</li>
                      ))}
                    </ul>
                    {firstOpens && (
                      <p className="mt-2 text-xs text-slate-500">
                        The first one opens {firstOpens <= toDateInput(new Date()) ? 'today' : formatUtcDate(firstOpens)}.
                      </p>
                    )}
                  </>
                ) : (
                  <p className="mt-1 text-slate-500">No due dates left{recurrence.endDate ? ' before the end date' : ''}.</p>
                )}
              </>
            ) : (
              <p className="text-slate-500">The next due dates appear here.</p>
            )}
          </div>

          <Switch
            checked={form.enabled}
            onChange={(enabled) => update({ enabled })}
            label="Turned on"
            description="A paused schedule doesn’t open anything until it’s turned back on."
          />
        </section>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Schedules
// ---------------------------------------------------------------------------

function SchedulesTab({ kind, onKind }: { kind: KindFilter; onKind(kind: KindFilter): void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<FacilitySchedule | 'new' | null>(null);
  const [deleting, setDeleting] = useState<FacilitySchedule | null>(null);
  const schedules = useQuery({
    queryKey: ['facilities', 'schedules', kind],
    queryFn: () => api.get<ScheduleList>('/facilities/schedules', { kind: kind === 'ALL' ? undefined : kind }),
    placeholderData: (previous) => previous,
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['facilities'] });

  const openNow = useMutation({
    mutationFn: (id: string) => api.post<OpenedOccurrence>(`/facilities/schedules/${id}/open`),
    onSuccess: (opened) => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      toast.success(opened.type === 'WORK_ORDER' ? `Opened work order #${opened.number}` : 'Opened the inspection');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => api.patch<FacilityScheduleDetail>(`/facilities/schedules/${id}`, { enabled }),
    onSuccess: (schedule) => {
      refresh();
      toast.success(schedule.enabled ? `Resumed “${schedule.name}”` : `Paused “${schedule.name}”`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/facilities/schedules/${id}`),
    onSuccess: () => {
      refresh();
      setDeleting(null);
      toast.success('Schedule deleted');
    },
    onError: (err) => {
      setDeleting(null);
      toast.error(errorMessage(err));
    },
  });

  const data = schedules.data;
  const permissions = data?.permissions;
  const showActions = !!permissions && (permissions.open || permissions.manage);
  const newButton = (size: 'sm' | 'md') =>
    permissions?.manage ? (
      <Button size={size} variant="primary" icon={<Plus />} onClick={() => setEditing('new')}>
        New schedule
      </Button>
    ) : undefined;

  return (
    <Card>
      <CardHeader
        title="Schedules"
        description="Each schedule opens its work order or inspection a set number of days before it’s due."
        actions={newButton('sm')}
      />
      <div className="border-b border-slate-100 px-5 py-3">
        <Segmented<KindFilter>
          className="w-full sm:max-w-lg"
          value={kind}
          onChange={onKind}
          options={[
            { value: 'ALL', label: 'All' },
            { value: 'PREVENTIVE_MAINTENANCE', label: 'Preventive maintenance' },
            { value: 'INSPECTION', label: 'Inspections' },
          ]}
        />
      </div>

      {!data ? (
        schedules.error ? (
          <ErrorState error={schedules.error} onRetry={() => void schedules.refetch()} />
        ) : (
          <PageLoader />
        )
      ) : data.items.length === 0 ? (
        <EmptyState
          icon={<CalendarClock />}
          title={kind === 'INSPECTION' ? 'No inspection schedules' : kind === 'PREVENTIVE_MAINTENANCE' ? 'No preventive maintenance schedules' : 'No schedules yet'}
          description="For example: replace the rooftop unit filters every 3 months, or inspect the playground every Monday."
          action={newButton('md')}
        />
      ) : (
        <Table className={cn('transition-opacity', schedules.isPlaceholderData && 'opacity-60')}>
          <thead>
            <tr>
              <Th>Schedule</Th>
              <Th className="hidden md:table-cell">Where</Th>
              <Th className="hidden md:table-cell">Assignee</Th>
              <Th>Next due</Th>
              <Th>State</Th>
              <Th className="hidden xl:table-cell">Currently open</Th>
              {showActions && (
                <Th className="text-right">
                  <span className="sr-only">Actions</span>
                </Th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {data.items.map((schedule) => {
              const muted = !schedule.enabled && 'opacity-60';
              const place = placeLabel(schedule);
              const open = schedule.openOccurrence;
              const canOpenNow = data.permissions.open && schedule.enabled && !open && !!schedule.nextDueOn;
              return (
                <tr key={schedule.id} className={cn(!schedule.enabled && 'bg-slate-50/60')}>
                  <Td className={cn('max-w-xs', muted)}>
                    <p className="font-medium text-slate-900">{schedule.name}</p>
                    <p className="text-xs text-slate-500">{schedule.recurrence}</p>
                    <p className="text-xs text-slate-500">
                      {[SCHEDULE_KIND_LABEL[schedule.kind], schedule.kind === 'INSPECTION' ? schedule.template?.name : schedule.department?.name]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </Td>
                  <Td className={cn('hidden max-w-[14rem] md:table-cell', muted)}>
                    {place ? <p>{place}</p> : !schedule.facilityAsset && '—'}
                    {schedule.facilityAsset && (
                      <Link to={equipmentPath(schedule.facilityAsset.id)} className="text-xs text-brand-700 hover:underline">
                        {equipmentLabel(schedule.facilityAsset)}
                      </Link>
                    )}
                  </Td>
                  <Td className={cn('hidden md:table-cell', muted)}>{schedule.assignedTo?.name ?? '—'}</Td>
                  <Td className={cn('whitespace-nowrap', muted)}>
                    {schedule.nextDueOn ? (
                      <>
                        <p>{formatUtcDate(schedule.nextDueOn)}</p>
                        {schedule.opensOn && <p className="text-xs text-slate-500">opens {formatUtcDate(schedule.opensOn)}</p>}
                      </>
                    ) : (
                      '—'
                    )}
                  </Td>
                  <Td className={muted || undefined}>
                    <div className="flex flex-wrap gap-1">
                      <ScheduleStateBadge state={schedule.state} />
                      {schedule.missedCount > 0 && (
                        <span
                          title={`Skipped ${schedule.missedCount} time${schedule.missedCount === 1 ? '' : 's'}. An occurrence is skipped when the one before it is still open after its due date.`}
                        >
                          <Badge tone="amber">Skipped {schedule.missedCount}</Badge>
                        </span>
                      )}
                    </div>
                  </Td>
                  <Td className={cn('hidden whitespace-nowrap xl:table-cell', muted)}>
                    {open ? (
                      <>
                        <Link to={occurrencePath(open)} className="text-brand-700 hover:underline">
                          {open.type === 'WORK_ORDER' ? `Work order #${open.number}` : 'Inspection'}
                        </Link>
                        {(open.dueOn || open.dueAt) && (
                          <p className="text-xs text-slate-500">due {open.dueOn ? formatUtcDate(open.dueOn) : formatDue(open.dueAt)}</p>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </Td>
                  {showActions && (
                    <Td className="text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-0.5">
                        {canOpenNow && (
                          <Button
                            size="sm"
                            variant="ghost"
                            icon={<CalendarPlus />}
                            loading={openNow.isPending && openNow.variables === schedule.id}
                            disabled={openNow.isPending}
                            onClick={() => openNow.mutate(schedule.id)}
                            title={`Open the ${formatUtcDate(schedule.nextDueOn)} ${schedule.kind === 'INSPECTION' ? 'inspection' : 'work order'} now instead of waiting`}
                          >
                            Open now
                          </Button>
                        )}
                        {data.permissions.manage && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              icon={schedule.enabled ? <Pause /> : <Play />}
                              loading={toggle.isPending && toggle.variables?.id === schedule.id}
                              onClick={() => toggle.mutate({ id: schedule.id, enabled: !schedule.enabled })}
                              aria-label={`${schedule.enabled ? 'Pause' : 'Resume'} ${schedule.name}`}
                              title={schedule.enabled ? 'Pause: stop opening work until it’s resumed' : 'Resume: start opening work again'}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              icon={<Pencil />}
                              onClick={() => setEditing(schedule)}
                              aria-label={`Edit ${schedule.name}`}
                              title="Edit"
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              icon={<Trash2 />}
                              onClick={() => setDeleting(schedule)}
                              aria-label={`Delete ${schedule.name}`}
                              title="Delete"
                            />
                          </>
                        )}
                      </div>
                    </Td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <ScheduleModal open={editing !== null} schedule={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        loading={remove.isPending}
        title={`Delete “${deleting?.name}”?`}
        description="Work orders and inspections it already opened stay. It won’t open any more."
        confirmLabel="Delete schedule"
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function MaintenancePage() {
  const [state, setState] = useUrlState({ tab: 'due', days: '14', mine: '', building: '', kind: 'ALL' });
  const tab: Tab = state.tab === 'schedules' ? 'schedules' : 'due';
  const days = ((DAY_OPTIONS as readonly string[]).includes(state.days) ? state.days : '14') as Days;
  const kind = (['PREVENTIVE_MAINTENANCE', 'INSPECTION'].includes(state.kind) ? state.kind : 'ALL') as KindFilter;

  return (
    <>
      <PageHeader title="Maintenance" description="Preventive maintenance and recurring inspections, and what’s due" />
      <Tabs<Tab>
        className="mb-6"
        value={tab}
        onChange={(value) => setState({ tab: value })}
        tabs={[
          { value: 'due', label: 'Due and overdue' },
          { value: 'schedules', label: 'Schedules' },
        ]}
      />
      {tab === 'due' ? (
        <DueTab days={days} mine={state.mine === '1'} buildingId={state.building} onFilter={(patch) => setState(patch)} />
      ) : (
        <SchedulesTab kind={kind} onKind={(value) => setState({ kind: value })} />
      )}
    </>
  );
}
